// ============================================================
// PROJECT TASK CALCULATIONS
// ============================================================

/**
 * Get status in lowercase safely
 */
const getStatus = (item) => {
  return String(
    item?.status ||
      item?.task_status ||
      item?.item_status ||
      ""
  )
    .trim()
    .toLowerCase();
};

/**
 * Get task due date
 */
const getDueDate = (item) => {
  return (
    item?.due_date ||
    item?.dueDate ||
    item?.planned_end_date ||
    item?.end_date ||
    item?.deadline ||
    null
  );
};

/**
 * ============================================================
 * TOTAL TASKS
 * ============================================================
 */
export const getTotalProjectTasks = (items = []) => {
  if (!Array.isArray(items)) {
    return 0;
  }

  return items.length;
};

/**
 * ============================================================
 * COMPLETED TASKS
 * ============================================================
 */
export const getCompletedProjectTasks = (items = []) => {
  if (!Array.isArray(items)) {
    return 0;
  }

  return items.filter((item) => {
    const status = getStatus(item);

    return (
      status === "completed" ||
      status === "complete" ||
      status === "done"
    );
  }).length;
};

/**
 * ============================================================
 * IN PROGRESS TASKS
 * ============================================================
 */
export const getInProgressProjectTasks = (items = []) => {
  if (!Array.isArray(items)) {
    return 0;
  }

  return items.filter((item) => {
    const status = getStatus(item);

    return (
      status === "in progress" ||
      status === "in-progress" ||
      status === "in_progress" ||
      status === "active" ||
      status === "started"
    );
  }).length;
};

/**
 * ============================================================
 * OVERDUE TASKS
 * ============================================================
 */
export const getOverdueProjectTasks = (items = []) => {
  if (!Array.isArray(items)) {
    return 0;
  }

  const today = new Date();

  today.setHours(0, 0, 0, 0);

  return items.filter((item) => {
    const status = getStatus(item);

    // Completed tasks should not be overdue
    if (
      status === "completed" ||
      status === "complete" ||
      status === "done"
    ) {
      return false;
    }

    const dueDateValue = getDueDate(item);

    if (!dueDateValue) {
      return false;
    }

    const dueDate = new Date(dueDateValue);

    if (Number.isNaN(dueDate.getTime())) {
      return false;
    }

    dueDate.setHours(0, 0, 0, 0);

    return dueDate < today;
  }).length;
};

/**
 * ============================================================
 * PROJECT PROGRESS
 * ============================================================
 *
 * Progress =
 *
 * completed tasks / total tasks * 100
 *
 * ============================================================
 */
export const getProjectTaskProgress = (items = []) => {
  const totalTasks =
    getTotalProjectTasks(items);

  const completedTasks =
    getCompletedProjectTasks(items);

  if (totalTasks === 0) {
    return 0;
  }

  return Math.round(
    (completedTasks / totalTasks) * 100
  );
};

/**
 * ============================================================
 * COMPLETE TASK SUMMARY
 * ============================================================
 */
export const getProjectTaskSummary = (items = []) => {
  const totalTasks =
    getTotalProjectTasks(items);

  const completedTasks =
    getCompletedProjectTasks(items);

  const inProgressTasks =
    getInProgressProjectTasks(items);

  const overdueTasks =
    getOverdueProjectTasks(items);

  const progress =
    getProjectTaskProgress(items);

  return {
    totalTasks,
    completedTasks,
    inProgressTasks,
    overdueTasks,
    progress,
  };
};